import type { YouTubePlaylistConfig, YouTubeVideoCardItem } from '@/types/video'

export const defaultYouTubePlaylistConfig: YouTubePlaylistConfig = {
  playlistId:
    import.meta.env.VITE_YOUTUBE_PLAYLIST_ID ||
    'PLUyVo9lCZvtMYPoswjJf2HPDZhcHFRbnw',
  featuredVideoId:
    import.meta.env.VITE_YOUTUBE_FEATURED_VIDEO_ID || '0RmkotOhZPk',
  apiKey: import.meta.env.VITE_YOUTUBE_API_KEY,
}

export function getYouTubeEmbedUrl(config = defaultYouTubePlaylistConfig) {
  const params = new URLSearchParams({
    listType: 'playlist',
    list: config.playlistId,
    rel: '0',
    enablejsapi: '1',
    playsinline: '1',
  })

  if (typeof window !== 'undefined') {
    params.set('origin', window.location.origin)
  }

  const videoId = config.featuredVideoId || 'videoseries'

  return `https://www.youtube.com/embed/${videoId}?${params.toString()}`
}

export function getYouTubeThumbnailUrl(videoId?: string) {
  return videoId ? `https://i.ytimg.com/vi/${videoId}/hqdefault.jpg` : undefined
}

function formatYouTubeDate(value?: string) {
  if (!value) return undefined

  const date = new Date(value)

  if (Number.isNaN(date.getTime())) return undefined

  return new Intl.DateTimeFormat('es-EC', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  }).format(date)
}

interface YouTubeThumbnail {
  url?: string
}

interface YouTubePlaylistItem {
  id?: string
  snippet?: {
    publishedAt?: string
    title?: string
    channelTitle?: string
    thumbnails?: {
      default?: YouTubeThumbnail
      medium?: YouTubeThumbnail
      high?: YouTubeThumbnail
      standard?: YouTubeThumbnail
      maxres?: YouTubeThumbnail
    }
    resourceId?: {
      videoId?: string
    }
  }
  contentDetails?: {
    videoId?: string
    videoPublishedAt?: string
  }
}

interface YouTubePlaylistItemResponse {
  items?: YouTubePlaylistItem[]
  nextPageToken?: string
}

interface YouTubeOEmbedResponse {
  title?: string
  author_name?: string
  thumbnail_url?: string
}

const playlistRequestCache = new Map<string, Promise<YouTubeVideoCardItem[]>>()
const featuredVideoRequestCache = new Map<string, Promise<YouTubeVideoCardItem>>()

function pickThumbnail(thumbnails?: NonNullable<YouTubePlaylistItem['snippet']>['thumbnails']) {
  return (
    thumbnails?.maxres?.url ??
    thumbnails?.standard?.url ??
    thumbnails?.high?.url ??
    thumbnails?.medium?.url ??
    thumbnails?.default?.url
  )
}

function normalizePlaylistItems(items: YouTubePlaylistItem[] = []) {
  return items.flatMap((item) => {
    const videoId = item.contentDetails?.videoId ?? item.snippet?.resourceId?.videoId

    if (!videoId || !item.snippet?.title) return []

    return [{
      id: item.id ?? videoId,
      title: item.snippet.title,
      eyebrow: 'Playlist',
      dateLabel: formatYouTubeDate(item.contentDetails?.videoPublishedAt ?? item.snippet.publishedAt),
      sourceLabel: item.snippet.channelTitle ?? 'YouTube',
      videoId,
      thumbnailUrl: pickThumbnail(item.snippet.thumbnails) ?? getYouTubeThumbnailUrl(videoId),
    }]
  })
}

async function fetchYouTubePlaylistPage(
  config: YouTubePlaylistConfig,
  maxResults: number,
  pageToken?: string,
) {
  const params = new URLSearchParams({
    part: 'snippet,contentDetails',
    playlistId: config.playlistId,
    maxResults: String(maxResults),
    key: config.apiKey ?? '',
    fields:
      'nextPageToken,items(id,snippet(publishedAt,title,channelTitle,thumbnails,resourceId),contentDetails(videoId,videoPublishedAt))',
  })

  if (pageToken) params.set('pageToken', pageToken)

  const response = await fetch(`https://www.googleapis.com/youtube/v3/playlistItems?${params.toString()}`)

  if (!response.ok) {
    throw new Error(`YouTube Data API respondió ${response.status}`)
  }

  return (await response.json()) as YouTubePlaylistItemResponse
}

async function loadYouTubePlaylistVideos(
  config: YouTubePlaylistConfig,
  limit: number,
) {
  const videos: YouTubeVideoCardItem[] = []
  const videoIds = new Set<string>()
  const visitedPageTokens = new Set<string>()
  let pageToken: string | undefined

  do {
    const remaining = Number.isFinite(limit) ? limit - videos.length : 50
    if (remaining <= 0) break

    const page = await fetchYouTubePlaylistPage(config, Math.min(50, remaining), pageToken)

    for (const video of normalizePlaylistItems(page.items)) {
      if (!video.videoId || videoIds.has(video.videoId)) continue
      videoIds.add(video.videoId)
      videos.push(video)
    }

    pageToken = page.nextPageToken
    if (!pageToken || visitedPageTokens.has(pageToken)) break
    visitedPageTokens.add(pageToken)
  } while (videos.length < limit)

  return videos
}

async function getFeaturedVideoMetadata(videoId: string): Promise<YouTubeVideoCardItem> {
  const cached = featuredVideoRequestCache.get(videoId)
  if (cached) return cached

  const request = fetchFeaturedVideoMetadata(videoId).catch((error) => {
    featuredVideoRequestCache.delete(videoId)
    throw error
  })

  featuredVideoRequestCache.set(videoId, request)
  return request
}

async function fetchFeaturedVideoMetadata(videoId: string): Promise<YouTubeVideoCardItem> {
  const response = await fetch(
    `https://www.youtube.com/oembed?${new URLSearchParams({
      url: `https://www.youtube.com/watch?v=${videoId}`,
      format: 'json',
    }).toString()}`,
  )

  if (!response.ok) {
    throw new Error(`YouTube oEmbed respondió ${response.status}`)
  }

  const data = (await response.json()) as YouTubeOEmbedResponse

  return {
    id: videoId,
    title: data.title ?? 'Video de YouTube',
    dateLabel: 'Fecha pendiente de API',
    sourceLabel: data.author_name ?? 'YouTube',
    videoId,
    thumbnailUrl: data.thumbnail_url ?? getYouTubeThumbnailUrl(videoId),
  }
}

async function getFeaturedVideoFallback(
  config: YouTubePlaylistConfig,
  count: number,
): Promise<YouTubeVideoCardItem[]> {
  if (!config.featuredVideoId) return []

  const featuredVideo = await getFeaturedVideoMetadata(config.featuredVideoId)

  return Array.from({ length: count }, (_, index) => ({
    ...featuredVideo,
    id: `${featuredVideo.id}-fallback-${index + 1}`,
    eyebrow: index === 0 ? featuredVideo.eyebrow : 'Playlist',
  }))
}

export async function getYouTubePlaylistVideos(
  config = defaultYouTubePlaylistConfig,
  maxResults = 3,
): Promise<YouTubeVideoCardItem[]> {
  if (!config.playlistId) return []

  const cacheKey = [
    config.playlistId,
    config.featuredVideoId ?? '',
    config.apiKey ? 'api' : 'oembed',
    String(maxResults),
  ].join(':')
  const cached = playlistRequestCache.get(cacheKey)
  if (cached) return cached

  const request = !config.apiKey
    ? getFeaturedVideoFallback(config, maxResults)
    : loadYouTubePlaylistVideos(config, maxResults).catch(() =>
        getFeaturedVideoFallback(config, maxResults),
      )

  const recoverableRequest = request.catch((error) => {
    playlistRequestCache.delete(cacheKey)
    throw error
  })

  playlistRequestCache.set(cacheKey, recoverableRequest)
  return recoverableRequest
}

const playlistCatalogCache = new Map<string, Promise<YouTubeVideoCardItem[]>>()

export function getYouTubePlaylistCatalog(
  config = defaultYouTubePlaylistConfig,
): Promise<YouTubeVideoCardItem[]> {
  if (!config.playlistId) return Promise.resolve([])

  const cacheKey = [config.playlistId, config.featuredVideoId ?? '', config.apiKey ? 'api' : 'oembed'].join(':')
  const cached = playlistCatalogCache.get(cacheKey)
  if (cached) return cached

  const request = config.apiKey
    ? loadYouTubePlaylistVideos(config, Number.POSITIVE_INFINITY).catch(() =>
        getFeaturedVideoFallback(config, 6),
      )
    : config.featuredVideoId
      ? getFeaturedVideoFallback(config, 6)
      : Promise.resolve([])

  const recoverableRequest = request.catch((error) => {
    playlistCatalogCache.delete(cacheKey)
    throw error
  })

  playlistCatalogCache.set(cacheKey, recoverableRequest)
  return recoverableRequest
}
